var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

import logger from '../util/logger';
import * as WebRTC from '../util/webrtc';
import { isDefined } from '../util/helpers';
import { DeviceType } from './constants';

const getUserMedia = constraints => __awaiter(void 0, void 0, void 0, function* () {
  logger.info('RTCService.getUserMedia', constraints);
  const {
    audio,
    video
  } = constraints;

  if (!audio && !video) {
    return null;
  }

  try {
    return yield WebRTC.getUserMedia(constraints);
  } catch (error) {
    logger.error('getUserMedia error: ', error);
    throw error;
  }
});

const _constraintsByKind = function () {
  let kind = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  return {
    audio: !kind || kind === DeviceType.AudioIn,
    video: !kind || kind === DeviceType.Video
  };
};

const getDevices = function () {
  let kind = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  let fullList = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return __awaiter(void 0, void 0, void 0, function* () {
    let devices = yield WebRTC.enumerateDevices().catch(error => []);

    if (kind) {
      devices = devices.filter(d => d.kind === kind);
    }

    const valid = devices.length && devices.every(d => d.deviceId && d.label);

    if (!valid) {
      const stream = yield WebRTC.getUserMedia(_constraintsByKind(kind));
      WebRTC.stopStream(stream);
      return getDevices(kind);
    }

    if (fullList === true) {
      return devices;
    }

    const found = [];
    devices = devices.filter(_ref => {
      let {
        kind,
        groupId
      } = _ref;

      if (!groupId) {
        return true;
      }

      const key = "".concat(kind, "-").concat(groupId);

      if (!found.includes(key)) {
        found.push(key);
        return true;
      }

      return false;
    });
    return devices;
  });
};

const resolutionList = [[320, 240], [640, 360], [640, 480], [1280, 720], [1920, 1080]];

const scanResolutions = deviceId => __awaiter(void 0, void 0, void 0, function* () {
  const supported = [];
  const stream = yield getUserMedia({
    video: {
      deviceId: {
        exact: deviceId
      }
    }
  });
  const videoTrack = stream.getVideoTracks()[0];

  for (let i = 0; i < resolutionList.length; i++) {
    const [width, height] = resolutionList[i];
    const success = yield videoTrack.applyConstraints({
      width: {
        exact: width
      },
      height: {
        exact: height
      }
    }).then(() => true).catch(() => false);

    if (success) {
      supported.push({
        resolution: "".concat(width, "x").concat(height),
        width,
        height
      });
    }
  }

  WebRTC.stopStream(stream);
  return supported;
});

const getMediaConstraints = options => __awaiter(void 0, void 0, void 0, function* () {
  let {
    audio = true,
    micId
  } = options;
  const {
    micLabel = ''
  } = options;

  if (micId) {
    micId = yield assureDeviceId(micId, micLabel, DeviceType.AudioIn).catch(error => null);

    if (micId) {
      if (typeof audio === 'boolean') {
        audio = {};
      }

      audio.deviceId = {
        exact: micId
      };
    }
  }

  let {
    video = false,
    camId
  } = options;
  const {
    camLabel = ''
  } = options;

  if (camId) {
    camId = yield assureDeviceId(camId, camLabel, DeviceType.Video).catch(error => null);

    if (camId) {
      if (typeof video === 'boolean') {
        video = {};
      }

      video.deviceId = {
        exact: camId
      };
    }
  }

  return {
    audio,
    video
  };
});

const assureDeviceId = (id, label, kind) => __awaiter(void 0, void 0, void 0, function* () {
  const devices = yield getDevices(kind, true);

  for (let i = 0; i < devices.length; i++) {
    const {
      deviceId,
      label: deviceLabel
    } = devices[i];

    if (id === deviceId || label === deviceLabel) {
      return deviceId;
    }
  }

  return null;
});

const removeUnsupportedConstraints = constraints => {
  const supported = WebRTC.getSupportedConstraints();
  Object.keys(constraints).map(key => {
    if (!supported.hasOwnProperty(key) || constraints[key] === null || constraints[key] === undefined) {
      delete constraints[key];
    }
  });
};

const checkDeviceIdConstraints = (id, label, kind, constraints) => __awaiter(void 0, void 0, void 0, function* () {
  const {
    deviceId
  } = constraints;

  if (!isDefined(deviceId) && (id || label)) {
    const deviceId = yield assureDeviceId(id, label, kind).catch(error => null);

    if (deviceId) {
      constraints.deviceId = {
        exact: deviceId
      };
    }
  }

  return constraints;
});

const sdpStereoHack = sdp => {
  const endOfLine = '\r\n';
  const sdpLines = sdp.split(endOfLine);
  const opusIndex = sdpLines.findIndex(s => /^a=rtpmap/.test(s) && /opus\/48000/.test(s));

  if (opusIndex < 0) {
    return sdp;
  }

  const getCodecPayloadType = line => {
    const pattern = new RegExp('a=rtpmap:(\\d+) \\w+\\/\\d+');
    const result = line.match(pattern);
    return result && result.length == 2 ? result[1] : null;
  };

  const opusPayload = getCodecPayloadType(sdpLines[opusIndex]);
  const pattern = new RegExp("a=fmtp:".concat(opusPayload));
  const fmtpLineIndex = sdpLines.findIndex(s => pattern.test(s));

  if (fmtpLineIndex >= 0) {
    if (!/stereo=1;/.test(sdpLines[fmtpLineIndex])) {
      sdpLines[fmtpLineIndex] += '; stereo=1; sprop-stereo=1';
    }
  } else {
    sdpLines[opusIndex] += "".concat(endOfLine, "a=fmtp:").concat(opusPayload, " stereo=1; sprop-stereo=1");
  }

  return sdpLines.join(endOfLine);
};

const _isAudioLine = line => /^m=audio/.test(line);

const _isVideoLine = line => /^m=video/.test(line);

const sdpMediaOrderHack = (answer, localOffer) => {
  const endOfLine = '\r\n';
  const offerLines = localOffer.split(endOfLine);
  const offerAudioIndex = offerLines.findIndex(_isAudioLine);
  const offerVideoIndex = offerLines.findIndex(_isVideoLine);

  if (offerAudioIndex < offerVideoIndex) {
    return answer;
  }

  const answerLines = answer.split(endOfLine);
  const answerAudioIndex = answerLines.findIndex(_isAudioLine);
  const answerVideoIndex = answerLines.findIndex(_isVideoLine);
  const audioLines = answerLines.slice(answerAudioIndex, answerVideoIndex);
  const videoLines = answerLines.slice(answerVideoIndex, answerLines.length - 1);
  const beginLines = answerLines.slice(0, answerAudioIndex);
  return [...beginLines, ...videoLines, ...audioLines, ''].join(endOfLine);
};

const checkSubscribeResponse = (response, channel) => {
  if (!response) {
    return false;
  }

  const {
    subscribed,
    alreadySubscribed
  } = destructSubscribeResponse(response);
  return subscribed.includes(channel) || alreadySubscribed.includes(channel);
};

const destructSubscribeResponse = response => {
  const tmp = {
    subscribed: [],
    alreadySubscribed: [],
    unauthorized: [],
    unsubscribed: [],
    notSubscribed: []
  };
  Object.keys(tmp).forEach(k => {
    tmp[k] = response["".concat(k, "Channels")] || [];
  });
  return tmp;
};

const enableAudioTracks = stream => {
  _updateMediaStreamTracks(stream, 'audio', true);
};

const disableAudioTracks = stream => {
  _updateMediaStreamTracks(stream, 'audio', false);
};

const toggleAudioTracks = stream => {
  _updateMediaStreamTracks(stream, 'audio', null);
};

const enableVideoTracks = stream => {
  _updateMediaStreamTracks(stream, 'video', true);
};

const disableVideoTracks = stream => {
  _updateMediaStreamTracks(stream, 'video', false);
};

const toggleVideoTracks = stream => {
  _updateMediaStreamTracks(stream, 'video', null);
};

const _updateMediaStreamTracks = function (stream) {
  let kind = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let enabled = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;

  if (!WebRTC.streamIsValid(stream)) {
    return null;
  }

  let tracks = [];

  switch (kind) {
    case 'audio':
      tracks = stream.getAudioTracks();
      break;

    case 'video':
      tracks = stream.getVideoTracks();
      break;

    default:
      tracks = stream.getTracks();
      break;
  }

  tracks.forEach(track => {
    switch (enabled) {
      case 'on':
      case true:
        track.enabled = true;
        break;

      case 'off':
      case false:
        track.enabled = false;
        break;

      default:
        track.enabled = !track.enabled;
        break;
    }
  });
};

const sdpBitrateHack = (sdp, max, min, start) => {
  const endOfLine = '\r\n';
  const lines = sdp.split(endOfLine);
  lines.forEach((line, i) => {
    if (/^a=fmtp:\d*/.test(line)) {
      lines[i] += ";x-google-max-bitrate=".concat(max, ";x-google-min-bitrate=").concat(min, ";x-google-start-bitrate=").concat(start);
    } else if (/^a=mid:(1|video)/.test(line)) {
      lines[i] += "\r\nb=AS:".concat(max);
    }
  });
  return lines.join(endOfLine);
};

export { getUserMedia, getDevices, scanResolutions, getMediaConstraints, assureDeviceId, removeUnsupportedConstraints, checkDeviceIdConstraints, sdpStereoHack, sdpMediaOrderHack, sdpBitrateHack, checkSubscribeResponse, destructSubscribeResponse, enableAudioTracks, disableAudioTracks, toggleAudioTracks, enableVideoTracks, disableVideoTracks, toggleVideoTracks };